import { useMemo } from 'react';
import { ApolloError, useLazyQuery, useQuery } from '@apollo/client';

import {
  PROPERTY_SUBTYPES_QUERY,
  PROPERTY_SUBTYPEDETAILS_QUERY,
  PROPERTY_OWNER_OCCUPIED_DATA,
} from 'graphql/property';
import { IdName } from 'interfaces/IdName';
import { IPropertySubType } from 'interfaces/IPropertySubType';
import { ErrorLogger } from 'services/ErrorLogger';

export const useSubTypes = (subType: IdName | undefined) => {
  const { data: dataSubtypes, loading } = useQuery<{
    propertySubtypes: IPropertySubType[];
  }>(PROPERTY_SUBTYPES_QUERY, {
    skip: !subType?.id,
    variables: {
      search: {
        typeId: subType?.id,
      },
    },
  });

  const propertySubtypes = useMemo(() => dataSubtypes?.propertySubtypes || [], [
    dataSubtypes,
  ]);

  return {
    propertySubtypes,
    loading,
  };
};

export const useLazySubtypes = () => {
  const [get, { data, loading }] = useLazyQuery<{
    propertySubtypes: IPropertySubType[];
  }>(PROPERTY_SUBTYPES_QUERY, {
    onError: (error: ApolloError) => {
      ErrorLogger.log(error.message);
      console.error('Unable to load property subtypes', error.message);
    },
  });
  return { get, data, loading };
};

export const useSubtypeDetails = (subType: IdName | undefined) => {
  const { data, loading } = useQuery(PROPERTY_SUBTYPEDETAILS_QUERY, {
    variables: {
      search: {
        filter: {
          propertySubtypeId: subType?.id,
        },
      },
    },
    skip: !subType,
    onError: (error: ApolloError) => {
      ErrorLogger.log(error.message);
      console.error('Unable to load property subtype details', error.message);
    },
  });
  return { data, loading };
};

export const useOwnerOccupiedData = () => {
  const { data: ownerOccupiedData, loading } = useQuery<{
    ownerOccupied: IdName[];
  }>(PROPERTY_OWNER_OCCUPIED_DATA, {
    fetchPolicy: 'network-only',
  });

  return {
    ownerOccupiedData: ownerOccupiedData?.ownerOccupied ?? [],
    loading,
  };
};
